import {Exclude, Expose, instanceToPlain, plainToInstance, Transform} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import {FlowMeterType, MeterType, toFlowMeterType, toMeterType} from "./meter";

export const MeterFlowVolumeTotalValidator = t.intersection([
  t.type({
    meterId: t.string,
    quantity: t.number,
    unitOfMeasure: t.string,
    freightTypeId: t.string,
    regionId: t.string,
  }),
  t.partial({
    quantityOverride: t.number,
    quantityOverrideComment: t.string,
    meterName: t.string,
    meterType: t.string,
    flowMeterType: t.string,
    freightTypeName: t.string,
    meterObjectId: t.string,
    meterObjectType: t.string,
    fromDttm: t.number,
    toDttm: t.number,
  }),
]);

export const MeterFlowVolumeTotalArrayValidator = t.array(MeterFlowVolumeTotalValidator);

export type MeterFlowVolumeTotal = t.TypeOf<typeof MeterFlowVolumeTotalValidator>;
export type MeterFlowVolumeTotalArray = t.TypeOf<typeof MeterFlowVolumeTotalArrayValidator>;

@Exclude()
export class MeterFlowVolumeTotalEntity {
  @Expose()
  meterId!: string;

  @Expose()
  meterName?: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toMeterType))
  meterType?: MeterType

  @Expose()
  @Transform(getDatabaseCodeTransformer(toFlowMeterType))
  flowMeterType?: FlowMeterType

  @Expose()
  meterObjectId?: string

  @Expose()
  meterObjectType?: string

  @Expose()
  quantity!: number;

  @Expose()
  freightTypeId!: string;

  @Expose()
  freightTypeName?: string;

  @Expose()
  unitOfMeasure!: string;

  @Expose()
  regionId!: string;

  @Expose()
  fromDttm?: number;

  @Expose()
  toDttm?: number;

  /**
   * Constructor for MeterFlowVolumeTotalEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: MeterFlowVolumeTotal): MeterFlowVolumeTotalEntity {
    return plainToInstance(MeterFlowVolumeTotalEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<MeterFlowVolumeTotal> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(MeterFlowVolumeTotalValidator, object);
  }

  asTypeValue(): MeterFlowVolumeTotal {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(MeterFlowVolumeTotalValidator, object);
  }
}

export const TemporalMeterFlowVolumeTotalEntryValidator = t.intersection([
  t.type({}),
  t.partial({
    quantity: t.number,
    unitOfMeasure: t.string,
    percentChange: t.number,
    fromDttm: t.number,
    toDttm: t.number,
  }),
]);

export const TemporalMeterFlowVolumeTotalEntryArrayValidator = t.array(TemporalMeterFlowVolumeTotalEntryValidator);

export type TemporalMeterFlowVolumeTotalEntry = t.TypeOf<typeof TemporalMeterFlowVolumeTotalEntryValidator>;
export type TemporalMeterFlowVolumeTotalEntryArray = t.TypeOf<typeof TemporalMeterFlowVolumeTotalEntryArrayValidator>;

export const TemporalMeterFlowVolumeTotalValidator = t.intersection([
  t.type({
    meterId: t.string,
    totalsByDttm: t.record(t.number, TemporalMeterFlowVolumeTotalEntryValidator),
  }),
  t.partial({
    meterName: t.string,
    meterType: t.string,
    flowMeterType: t.string,
    meterObjectId: t.string,
    meterObjectType: t.string,
  }),
]);

export const TemporalMeterFlowVolumeTotalArrayValidator = t.array(TemporalMeterFlowVolumeTotalValidator);

export type TemporalMeterFlowVolumeTotal = t.TypeOf<typeof TemporalMeterFlowVolumeTotalValidator>;
export type TemporalMeterFlowVolumeTotalArray = t.TypeOf<typeof TemporalMeterFlowVolumeTotalArrayValidator>;

export const TemporalMeterFlowVolumeTotalMatrixValidator = t.intersection([
  t.type({
    dttms: t.array(t.number),
    totals: TemporalMeterFlowVolumeTotalArrayValidator,
  }),
  t.partial({}),
]);

export type TemporalMeterFlowVolumeTotalMatrix = t.TypeOf<typeof TemporalMeterFlowVolumeTotalMatrixValidator>;
